"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight } from "@/components/icons";
import { SectionHeader } from "@/components/ui";
import { ROUTES } from "@/lib/constants";

const MotionDiv = motion.div;

export interface RelatedUseCase {
  slug: string;
  label: string;
}

interface Props {
  useCases: RelatedUseCase[];
}

export default function AlternativesRelatedUseCasesLinks({ useCases }: Props) {
  const prefersReducedMotion = useReducedMotion();

  if (useCases.length === 0) return null;

  return (
    <section
      className="full-bleed band-wash bg-band py-section border-border border-b"
      aria-labelledby="related-use-cases-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55 }}
          className="mb-10 max-w-2xl"
        >
          <SectionHeader
            eyebrow="Use Cases"
            id="related-use-cases-heading"
            heading="See what else Proxidize is built for."
            body="Mobile and residential proxies power more than head-to-head comparisons — explore the workloads teams run on Proxidize every day."
          />
        </MotionDiv>

        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="flex flex-wrap gap-3"
        >
          {useCases.map((useCase) => (
            <Link
              key={useCase.slug}
              href={`${ROUTES.useCases}/${useCase.slug}`}
              className="border-border bg-card text-foreground hover:border-brand/40 hover:bg-brand/5 inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors"
            >
              {useCase.label}
              <ArrowRight size={13} aria-hidden="true" />
            </Link>
          ))}
        </MotionDiv>

        <div className="mt-6">
          <Link
            href={ROUTES.useCases}
            className="text-brand hover:text-brand/80 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
          >
            See all use cases
            <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
